import React from 'react';
import { 
  Sparkles, CheckSquare, MessageSquare, Layers, Calendar, 
  Target, BarChart3, FolderOpen, ChevronDown, ChevronRight 
} from 'lucide-react';
import { SidebarNavItemsProps } from './types';

export const SidebarNavItems: React.FC<SidebarNavItemsProps> = React.memo(({
  localViewMode,
  isCollapsed,
  uiMode,
  isStrategyOpen,
  isStrategyActive,
  onToggleStrategy,
  onSelect,
  onPrefetch
}) => {
  return (
    <div className="px-3 pt-4 pb-2 space-y-1 bg-transparent border-b border-slate-100 dark:border-slate-800/50 z-20">
      {/* KI Assistent */}
      <div 
        data-tour="ai-hub-tab"
        className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
          ${localViewMode === 'aiAssistant' 
            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
        `}
        onClick={() => onSelect('aiAssistant')}
        onMouseEnter={() => onPrefetch?.('aiAssistant')}
      >
        {localViewMode === 'aiAssistant' && (
          <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
        )}
        <Sparkles size={18} className={localViewMode === 'aiAssistant' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
        {!isCollapsed && <div className="text-sm font-bold">KI Assistent</div>}
      </div>

      {/* Aufgaben */}
      <div 
        className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
          ${localViewMode === 'globalKanban' 
            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
        `}
        onClick={() => onSelect('globalKanban')}
        onMouseEnter={() => onPrefetch?.('globalKanban')}
      >
        {localViewMode === 'globalKanban' && (
          <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
        )}
        <CheckSquare size={18} className={localViewMode === 'globalKanban' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
        {!isCollapsed && <div className="text-sm font-bold">Aufgaben</div>}
      </div>

      {/* Kommunikation */}
      <div 
        className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
          ${localViewMode === 'communication' 
            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
        `}
        onClick={() => onSelect('communication')}
        onMouseEnter={() => onPrefetch?.('communication')}
      >
        {localViewMode === 'communication' && (
          <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
        )}
        <MessageSquare size={18} className={localViewMode === 'communication' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
        {!isCollapsed && <div className="text-sm font-bold">Kommunikation</div>}
      </div>

      {/* Gantt */}
      <div 
        className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
          ${localViewMode === 'globalGantt' 
            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
        `}
        onClick={() => onSelect('globalGantt')}
      >
        {localViewMode === 'globalGantt' && (
          <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
        )}
        <Layers size={18} className={localViewMode === 'globalGantt' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
        {!isCollapsed && <div className="text-sm font-bold">Gantt</div>}
      </div>

      {/* Kalender */}
      <div 
        className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
          ${localViewMode === 'dashboard' 
            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
        `}
        onClick={() => onSelect('dashboard')}
        onMouseEnter={() => onPrefetch?.('dashboard')}
      >
        {localViewMode === 'dashboard' && (
          <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
        )}
        <Calendar size={18} className={localViewMode === 'dashboard' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
        {!isCollapsed && <div className="text-sm font-bold">Kalender</div>}
      </div>

      {/* Strategie & Analyse Section (Collapsible in Standard Mode, Direct in Expert Mode) */}
      {uiMode === 'standard' && !isCollapsed ? (
        <div className="pt-2 pb-1 border-t border-slate-200/40 dark:border-slate-800/40 mt-1">
          <button
            type="button"
            onClick={onToggleStrategy}
            className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100/50 dark:hover:bg-slate-800/40 transition-colors group mb-1"
            title="Erweiterte Analyse- und Strategiewerkzeuge ein-/ausblenden"
          >
            <div className="flex items-center gap-2">
              <Target size={14} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
              <span>Strategie & Analyse</span>
              {isStrategyActive && !isStrategyOpen && (
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
              )}
            </div>
            {isStrategyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          
          {isStrategyOpen && (
            <div className="space-y-1 pl-1 animate-in fade-in slide-in-from-top-1 duration-150">
              {/* Analyse */}
              <div 
                data-tour="analysis-tab"
                className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
                  ${localViewMode === 'analysis'
                    ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
                `}
                onClick={() => onSelect('analysis')}
                onMouseEnter={() => onPrefetch?.('analysis')}
              >
                {localViewMode === 'analysis' && (
                  <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
                )}
                <Target size={18} className={localViewMode === 'analysis' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
                <div className="text-sm font-bold">Analyse</div>
              </div>

              {/* Fortschritt */}
              <div 
                data-tour="progress-tab"
                className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
                  ${localViewMode === 'metricsDashboard' 
                    ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
                `}
                onClick={() => onSelect('metricsDashboard')}
                onMouseEnter={() => onPrefetch?.('metricsDashboard')}
              >
                {localViewMode === 'metricsDashboard' && (
                  <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
                )}
                <BarChart3 size={18} className={localViewMode === 'metricsDashboard' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
                <div className="text-sm font-bold">Fortschritt</div>
              </div>

              {/* Projektpool */}
              <div 
                className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
                  ${localViewMode === 'projectPool' 
                    ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
                `}
                onClick={() => onSelect('projectPool')}
                onMouseEnter={() => onPrefetch?.('projectPool')}
              >
                {localViewMode === 'projectPool' && (
                  <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
                )}
                <FolderOpen size={18} className={localViewMode === 'projectPool' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
                <div className="text-sm font-bold">Projektpool</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div 
            data-tour="analysis-tab"
            className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
              ${localViewMode === 'analysis'
                ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
            `}
            onClick={() => onSelect('analysis')}
            onMouseEnter={() => onPrefetch?.('analysis')}
          >
            {localViewMode === 'analysis' && (
              <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
            )}
            <Target size={18} className={localViewMode === 'analysis' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
            {!isCollapsed && <div className="text-sm font-bold">Analyse</div>}
          </div>

          <div 
            data-tour="progress-tab"
            className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
              ${localViewMode === 'metricsDashboard' 
                ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
            `}
            onClick={() => onSelect('metricsDashboard')}
            onMouseEnter={() => onPrefetch?.('metricsDashboard')}
          >
            {localViewMode === 'metricsDashboard' && (
              <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
            )}
            <BarChart3 size={18} className={localViewMode === 'metricsDashboard' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
            {!isCollapsed && <div className="text-sm font-bold">Fortschritt</div>}
          </div>

          <div 
            className={`flex items-center gap-3 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer relative overflow-hidden border hover:translate-x-[2px]
              ${localViewMode === 'projectPool' 
                ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50' 
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30'}
            `}
            onClick={() => onSelect('projectPool')}
            onMouseEnter={() => onPrefetch?.('projectPool')}
          >
            {localViewMode === 'projectPool' && (
              <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
            )}
            <FolderOpen size={18} className={localViewMode === 'projectPool' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} />
            {!isCollapsed && <div className="text-sm font-bold">Projektpool</div>}
          </div>
        </>
      )}
    </div>
  );
});
